import { PROJECT_PROFIT_MARGIN_TOOLTIP, PROJECT_PROFIT_TOOLTIP } from '../constants';
import { ReturnsMetricId } from './metrics';
import { BLANK_CELL, BLANK_PERCENTAGE_CELL, DASH_CELL } from './constants';
import { SectionDescriptor, RowDescriptor } from './types';

export const PROPERTY_RETURNS_COLUMNS = ['', 'ROI', 'EMx', 'IRR'] as const;
export const PARTNERSHIP_RETURNS_COLUMNS = ['Net proceeds', 'ROI', 'EMx', 'IRR'] as const;
export const DEVELOPMENT_RETURNS_COLUMNS = ['', 'Untrended', 'Trended', 'Sale'] as const;

type PropertyRow = RowDescriptor<ReturnsMetricId, typeof PROPERTY_RETURNS_COLUMNS>;
type PartnershipRow = RowDescriptor<ReturnsMetricId, typeof PARTNERSHIP_RETURNS_COLUMNS>;
type DevelopmentRow = RowDescriptor<ReturnsMetricId, typeof DEVELOPMENT_RETURNS_COLUMNS>;

const propertyRow = (id: string, label: string, rowKey: 'unlevered' | 'levered'): PropertyRow => ({
    id,
    label,
    cells: [BLANK_CELL, { metric: `${rowKey}.roi` }, { metric: `${rowKey}.emx` }, { metric: `${rowKey}.irr` }],
});

const partnershipRow = (id: string, rowKey: 'lp' | 'gp', fallbackLabel: string): PartnershipRow => ({
    id,
    label: { metric: `${rowKey}.name`, fallback: fallbackLabel },
    cells: [
        { metric: `${rowKey}.netProceeds` },
        { metric: `${rowKey}.roi` },
        { metric: `${rowKey}.emx` },
        { metric: `${rowKey}.irr` },
    ],
});

const trendingRow = (id: string, label: string, rowKey: 'yieldOnCost' | 'marketCapRate' | 'developmentSpread'): DevelopmentRow => ({
    id,
    label,
    cells: [
        BLANK_PERCENTAGE_CELL,
        { metric: `${rowKey}.untrended` },
        { metric: `${rowKey}.trended` },
        { metric: `${rowKey}.sale` },
    ],
});

const atSaleOnlyRow = (
    id: string,
    label: string,
    rowKey: 'projectProfit' | 'projectProfitMargin',
    tooltip: string,
): DevelopmentRow => ({
    id,
    label,
    tooltip,
    cells: [BLANK_PERCENTAGE_CELL, DASH_CELL, DASH_CELL, { metric: `${rowKey}.sale` }],
});

export const returnsLayout = (): SectionDescriptor<ReturnsMetricId, readonly string[]>[] => [
    {
        id: 'property-returns-section',
        title: 'Property returns',
        columns: PROPERTY_RETURNS_COLUMNS,
        rows: [
            propertyRow('unlevered-row', 'Unlevered', 'unlevered'),
            propertyRow('levered-row', 'Levered', 'levered'),
        ],
    },
    {
        id: 'partnership-returns-section',
        title: 'Partnership returns',
        columns: PARTNERSHIP_RETURNS_COLUMNS,
        rows: [
            partnershipRow('lp', 'lp', 'Limited Partner'),
            partnershipRow('gp1', 'gp', 'General Partner'),
        ],
    },
    {
        id: 'development-returns-section',
        title: 'Development returns',
        columns: DEVELOPMENT_RETURNS_COLUMNS,
        rows: [
            trendingRow('yield-on-cost-row', 'Yield-on-cost', 'yieldOnCost'),
            trendingRow('market-cap-rate-row', 'Market cap rate', 'marketCapRate'),
            trendingRow('development-spread-row', 'Development spread', 'developmentSpread'),
            atSaleOnlyRow('project-profit-row', 'Project profit', 'projectProfit', PROJECT_PROFIT_TOOLTIP),
            atSaleOnlyRow('project-profit-margin-row', 'Project profit margin', 'projectProfitMargin', PROJECT_PROFIT_MARGIN_TOOLTIP),
        ],
    },
];
